import type { CSSProperties } from 'react';
import { cn } from '~/lib/utils';
import { GRANOLA } from './granolaKit';
import styles from './GranolaSlideArt.module.css';

/**
 * The artifacts of the "Your brand, already applied" row: the three it shows the kit having made,
 * and the one the kit was made FROM — the brand guide the ask attaches.
 *
 * Authored at 1280x720 like every other artifact on this landing, and laid out FOR the kit rather
 * than recoloured into it: a cover that leads with the mark and the display serif, a text slide whose
 * accent does the marking, and a figure slide built on the ink. Each one uses a different pair of the
 * five colours, so three slides show the palette working three ways rather than the same way thrice.
 *
 * The words are Deck's — neutral deck content, the sort of thing a quarterly review says. Nothing in
 * here is a claim about granola: the brand is borrowed for the row, the sentences are not.
 */

export type GranolaSlideKind = 'guide' | 'cover' | 'points' | 'figure';

/** What a slide paints with. Runtime values — the brand's colours are content, never tokens. */
function ground(bg: string, ink: string, note: string): CSSProperties {
  return { '--gs-ground': bg, '--gs-ink': ink, '--gs-note': note, '--gs-accent': GRANOLA.accent } as CSSProperties;
}

export function GranolaSlide({ kind }: { kind: GranolaSlideKind }) {
  if (kind === 'guide') {
    /*
     * The brand guide's first page — the preview on the PDF chip in the conversation (Beka,
     * 2026-09-15). The cover's own ground and ink, because a guide is the brand at its loudest.
     *
     * Laid out for the chip it is drawn behind, not for a full-size look. The chip shows this page's
     * LEFT SQUARE, 720 of it at 96/720, and puts its own type in three places over it: the kind label
     * hanging from the top, the remove dot in the top-right corner, the name on the bottom edge. So
     * the page keeps all three clear and puts everything in the band between — the mark and the
     * title as one lockup on the page's middle, inset 75 so its edge is the chip's own 10px line.
     */
    return (
      <div
        className={cn(styles.slide, styles.guidePage)}
        style={ground(GRANOLA.accent, GRANOLA.ink, GRANOLA.ink)}
      >
        <span className={styles.spacer} />
        <div className={styles.guideLockup}>
          <img className={styles.guideMark} src={GRANOLA.logo} alt="" />
          <div className={styles.guideTitle}>
            Brand
            <br />
            guidelines
          </div>
        </div>
        <span className={styles.spacer} />
      </div>
    );
  }

  if (kind === 'cover') {
    /* The accent on the ground and the ink on top — the loudest of the three, which is what a cover
       is for. The muted grey is a colour for an off-white page and dies on the lime, so the meta
       line takes the ink at the weight that separates it instead. */
    return (
      <div className={styles.slide} style={ground(GRANOLA.accent, GRANOLA.ink, GRANOLA.ink)}>
        <img className={styles.coverMark} src={GRANOLA.logo} alt="" />
        <span className={styles.spacer} />
        <div className={styles.coverHead}>Where the quarter went</div>
        <div className={styles.coverMeta}>Q3 review · Product</div>
      </div>
    );
  }

  if (kind === 'points') {
    return (
      <div className={styles.slide} style={ground(GRANOLA.paper, GRANOLA.ink, GRANOLA.inkMuted)}>
        <div className={styles.label} style={{ color: GRANOLA.inkMuted }}>
          What changed
        </div>
        <span className={styles.spacer} />
        <div className={styles.points}>
          {['Fewer handoffs', 'One place to look', 'Decisions in writing'].map((line) => (
            <div key={line} className={styles.point}>
              <span className={styles.dot} />
              <span className={styles.pointText}>{line}</span>
            </div>
          ))}
        </div>
        <span className={styles.spacer} />
      </div>
    );
  }

  /* The ink as a ground, which is the one thing the other two never do — so the three read as a deck
     with a range rather than as one layout in three colourways. */
  return (
    <div className={styles.slide} style={ground(GRANOLA.ink, GRANOLA.paper, GRANOLA.inkMuted)}>
      <div className={styles.label} style={{ color: GRANOLA.accent }}>
        Time to ship
      </div>
      <span className={styles.spacer} />
      <div className={styles.figNumber}>11</div>
      <div className={styles.figUnit}>weeks</div>
      <div className={styles.figBars}>
        {[240, 172, 108].map((width, i) => (
          <i key={width} style={{ width, opacity: 1 - i * 0.32 }} />
        ))}
      </div>
      <div className={styles.figNote}>Two rounds, four rebuilds.</div>
    </div>
  );
}

/**
 * A plate: the artifact itself, scaled to fill whatever cell it is given.
 *
 * `width` is the MEASURED width of the slot, because the scale is `width / 1280` and the artifact
 * never reflows — the one rule that is easy to break by accident. The box takes the cell rather than
 * a size of its own, so the grid's row height comes from the caller's own 16:9 slot.
 */
export function GranolaSlidePlate({
  kind,
  width,
  className,
}: {
  kind: GranolaSlideKind;
  width: number;
  className?: string;
}) {
  return (
    <span
      className={cn(styles.plate, className)}
      style={{ '--plate-scale': `${width / 1280}` } as CSSProperties}
    >
      <span className={styles.plateScale}>
        <GranolaSlide kind={kind} />
      </span>
    </span>
  );
}

GranolaSlide.displayName = 'GranolaSlide';
